import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle } from 'lucide-react';
import { AccrualType, VacationBalance, VacationRequest } from '../../types';
import { VacationStatusBadge } from './VacationStatusBadge';
import { formatShortDate } from '../../utils/formatters';

interface VacationReviewDialogProps {
  request: VacationRequest | null;
  memberName?: string;
  balance?: VacationBalance | null;
  balanceLoading?: boolean;
  open: boolean;
  isSubmitting?: boolean;
  /** When true the dialog is a read-only preview: no comments box or approve/reject actions. */
  readOnly?: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (comments: string) => void;
  onReject: (comments: string) => void;
}

export const VacationReviewDialog: React.FC<VacationReviewDialogProps> = ({
  request,
  memberName,
  balance,
  balanceLoading,
  open,
  isSubmitting,
  readOnly = false,
  onOpenChange,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation('vacations');
  const [comments, setComments] = useState('');
  const [error, setError] = useState('');

  // Reset the comment/error state when the dialog opens or switches to a
  // different request, so nothing carries over from a previous review.
  const requestId = request?.id;
  useEffect(() => {
    if (open) {
      setComments('');
      setError('');
    }
  }, [open, requestId]);

  if (!request) return null;

  const isFlex = balance?.accrualType === AccrualType.FLEX;
  const isUnlimited = !!balance?.unlimited;
  // The days a reviewer should judge against depend on the accrual type:
  // - FLEX unlimited: no ceiling, so never insufficient.
  // - FLEX capped: accrued is always 0, so judge against the cap: annualAllowance - used.
  // - Accrued policies (YEARLY/MONTHLY/IMMEDIATE): `available` already nets PENDING, so add
  //   it back to judge against APPROVED usage only (approvedAvailable = available + pending).
  const approvedAvailable = balance
    ? isFlex
      ? balance.annualAllowance - balance.used
      : balance.available + balance.pending
    : 0;
  const insufficient = !!balance && !isUnlimited && approvedAvailable < request.workingDays;

  const handleApprove = () => {
    setError('');
    onApprove(comments.trim());
  };

  const handleReject = () => {
    if (!comments.trim()) {
      setError(t('review.rejectRequiresComment'));
      return;
    }
    setError('');
    onReject(comments.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vacation-review-dialog">
        <DialogHeader>
          <DialogTitle>{readOnly ? t('view.title') : t('review.title')}</DialogTitle>
          <DialogDescription>
            {readOnly ? t('view.description') : t('review.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {memberName && (
              <div className="col-span-2">
                <span className="text-muted-foreground">{t('review.member')}: </span>
                <span className="font-medium">{memberName}</span>
              </div>
            )}
            <div className="col-span-2">
              <span className="text-muted-foreground">{t('review.requestedOn')}: </span>
              <span className="font-medium">{formatShortDate(request.createdAt)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('review.startDate')}: </span>
              <span className="font-medium">{formatShortDate(request.startDate)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('review.endDate')}: </span>
              <span className="font-medium">{formatShortDate(request.endDate)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('review.workingDays')}: </span>
              <span className="font-medium">{request.workingDays}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('review.status')}: </span>
              <VacationStatusBadge status={request.status} />
            </div>
            {request.notes && (
              <div className="col-span-2">
                <span className="text-muted-foreground">{t('review.notes')}: </span>
                <span>{request.notes}</span>
              </div>
            )}
            {request.reviewerComments && (
              <div className="col-span-2">
                <span className="text-muted-foreground">{t('card.reviewerComment')}: </span>
                <span>{request.reviewerComments}</span>
              </div>
            )}
            {request.cancellationReason && (
              <div className="col-span-2">
                <span className="text-muted-foreground">{t('view.cancellationReason')}: </span>
                <span>{request.cancellationReason}</span>
              </div>
            )}
          </div>

          {!readOnly && (
            <>
              <div className="rounded-md bg-muted p-3 text-sm">
                {balanceLoading ? (
                  <span className="text-muted-foreground">{t('review.loadingBalance')}</span>
                ) : balance ? (
                  <div className="flex items-center justify-between">
                    <span>{t('review.availableBalance')}</span>
                    <span className="font-semibold">
                      {isUnlimited
                        ? t('review.unlimited')
                        : `${approvedAvailable} / ${request.workingDays} ${t('review.daysRequested')}`}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{t('review.noBalance')}</span>
                )}
              </div>

              {insufficient && (
                <Alert variant="destructive" data-testid="vacation-review-insufficient">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('review.insufficientBalance')}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="review-comments">{t('review.comments')}</Label>
                <Textarea
                  id="review-comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={t('review.commentsPlaceholder')}
                  data-testid="vacation-review-comments"
                />
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {readOnly ? (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="vacation-view-close"
            >
              {t('actions.close')}
            </Button>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isSubmitting}
                data-testid="vacation-review-reject"
              >
                {t('actions.reject')}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={isSubmitting}
                data-testid="vacation-review-approve"
              >
                {t('actions.approve')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
